import { config } from "@/config";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { useQuery, useMutation, useQueryClient, type Query } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";

export type FileGraphStatus = 'started' | 'node_creation' | 'relationship_creation' | 'creating_embeddings' | 'metadata_addition' | 'active' | 'error';

// Legacy status type (deprecated - use OntologyStatusEnum instead)
export type FunctionalGraphStatus = 'inProgress' | 'error' | 'active';

// New status enum matching document statuses
export type OntologyStatusEnum = 'none' | 'inprogress' | 'done' | 'error';

export interface CodeOntology {
  _id: string | number;
  name: string;
  language: string;
  projectUuid: string;
  createdAt: string;
  updatedAt: string;
  status?: string;
  userId?: number;
  fileCount?: number;
  functionCount?: number;
  classCount?: number;
  totalLinesOfCode?: number;
  repositoryPath?: string;
  repositoryName?: string;
  repoUrl?: string;
  gitBranch?: string;
  gitProvider?: string;
  gitToken?: string;
  languages?: string[];
  metadata?: unknown;
  fileGraphStatus?: FileGraphStatus;
  statusMessage?: string | null;
  /** Which pipeline step failed, written beside `fileGraphStatus: "error"`.
   *  Known values: `embeddings`, `embedding_drain`, `clustering`,
   *  `metadata_update`, `repository_import`, `repository_update`, `s3_import`,
   *  `diff_analysis`, `initial_sync_trigger`. Drives which recovery action the
   *  list page offers (BREEZEAI-757) — see `lib/code-ontology-retry.ts`. */
  failedStep?: string | null;
  /** When the failure was recorded. Null on rows that errored before the
   *  column existed. */
  erroredAt?: string | null;
  /** Last sign of life from a running ingest, stamped once per BATCH
   *  (BREEZEAI-757). Unlike `updatedAt`, which only moves on a stage
   *  transition, this distinguishes a large import that is still working from
   *  one that has stalled. Null on rows last ingested before the column
   *  existed — readers fall back to `updatedAt`. */
  progressAt?: string | null;
  // Legacy keys (deprecated)
  functionalGraphStatus?: FunctionalGraphStatus;
  architectureGraphStatus?: FunctionalGraphStatus;
  // New keys with standardized status values
  funcOntolgyStatus?: OntologyStatusEnum;
  arcOntologyStatus?: OntologyStatusEnum;
  clusterCount?: number;
  commitId?: string;
  /** The commit the functional, design and architecture graphs are up to date with, set by hand
   *  (BREEZEAI-1160). Distinct from `commitId`, the code graph's commit.
   *  null = not set yet. */
  ontologyCommitId?: string | null;
  /** .repoignore-style patterns stored at connect time and reused on every
   *  sync (BREEZEAI-1099). Newline-separated; null/undefined when none. */
  ignorePatterns?: string | null;
}

export type SortOrder = "asc" | "desc";

interface GetOntologiesParams {
  projectUuid: string;
  page?: number;
  limit?: number;
  sortName?: string;
  sortOrder?: SortOrder;
}

interface CreateOntologyParams {
  file: File;
  projectUuid: string;
  name: string;
  repoUrl?: string;
  gitToken?: string;
  gitBranch?: string;
}

interface CreateOntologyFromGitParams {
  projectUuid: string;
  name: string;
  repoUrl: string;
  gitToken?: string;
  gitBranch: string;
  gitProvider?: string;
  /** Optional .repoignore-style patterns, newline-separated. Applied in addition
   *  to the analyzer's built-in defaults and the repo's own .gitignore. */
  ignorePatterns?: string;
}

interface UpdateOntologyParams {
  id: string;
  projectUuid: string;
  file?: File;
  name?: string;
  repoUrl?: string;
  gitToken?: string;
  gitBranch?: string;
  gitProvider?: string;
  commitId?: string;
  /** Newline-separated patterns; "" clears the stored value. Only sent by the
   *  Edit Git Config flow (BREEZEAI-1238 DEF-02). */
  ignorePatterns?: string;
}

interface TestGitConnectionParams {
  repoUrl: string;
  gitToken?: string;
  gitBranch: string;
}

export interface CheckUpdateResponse {
  change: boolean;
  latestCommitId: string;
  currentCommitId: string;
  fileUpdatedCount: number;
  fileDeletedCount: number;
  /** The commit the functional, design and architecture graphs are up to date with. */
  ontologyCommitId?: string | null;
  /** True when no ontology commit is set, so no diff document can be made yet. */
  noOntologyCommit?: boolean;
  /** True when commits landed since the last diff document. null while the
   *  code graph itself is behind the repo and has to be synced first. */
  ontologyBehind?: boolean | null;
}

interface DeleteOntologyParams {
  id: string;
  projectUuid: string;
}

interface SearchOntologyParams {
  query: string;
  projectUuid: string;
  page?: number;
  limit?: number;
  sortName?: string;
  sortOrder?: SortOrder;
}

interface GetOntologiesResponse {
  data: CodeOntology[];
  total: number;
}

// Mirrors `DocumentServiceRefetchInterval` (use-get-document-service.ts): the
// function form lets a caller read the just-fetched rows (via `query.state.data`)
// and decide the next interval — e.g. poll while something is in progress and
// stop otherwise — without a second query just to carry that signal.
export type CodeOntologyRefetchInterval =
  | number
  | false
  | ((
      query: Query<GetOntologiesResponse, Error>,
    ) => number | false | undefined);

/**
 * Guard against sending a missing/invalid projectUuid to the backend.
 * Catches cases where an undefined value would be stringified into the URL
 * (e.g. `projectUuid=undefined`).
 */
function assertProjectUuid(projectUuid: string | undefined | null): asserts projectUuid is string {
  if (!projectUuid || projectUuid === "undefined" || projectUuid === "null") {
    throw new Error("Missing projectUuid for code ontology request.");
  }
}

const MAX_ONTOLOGY_NAME_LENGTH = 255;
const INVALID_ONTOLOGY_NAME_CHARS = /[<>"';]|--/;

// Mirrors the backend's IsIgnorePatternList rule (BREEZEAI-1238 DEF-01): by
// default only the HTML tag delimiters are rejected. Quotes, `&`, `;` and `|`
// are legal in file names and `#` comments, so they stay allowed. The limits
// live in `config` (env-overridable: IGNORE_PATTERN_INVALID_CHARS,
// IGNORE_PATTERN_MAX_LINE_LENGTH, IGNORE_PATTERNS_MAX_TOTAL_LENGTH).

/** "<>" → "< or >", "<>&" → "<, > or &" — for the invalid-character message. */
function describeInvalidChars(chars: string): string {
  const list = Array.from(chars);
  if (list.length <= 1) return list.join("");
  return `${list.slice(0, -1).join(", ")} or ${list[list.length - 1]}`;
}

/**
 * Return a user-facing error for an ignore-patterns textarea value, or null
 * when it is acceptable. Blank input is acceptable (it means "no patterns").
 */
export function getIgnorePatternsError(value: string): string | null {
  const {
    ignorePatternInvalidChars,
    ignorePatternInvalidCharsRegex,
    ignorePatternMaxLineLength,
    ignorePatternsMaxTotalLength,
  } = config;
  if (value.length > ignorePatternsMaxTotalLength) {
    return `Ignore patterns must be ${ignorePatternsMaxTotalLength} characters or fewer in total.`;
  }
  for (const raw of value.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    if (line.length > ignorePatternMaxLineLength) {
      return `Each ignore pattern must be ${ignorePatternMaxLineLength} characters or fewer.`;
    }
    if (ignorePatternInvalidCharsRegex?.test(line)) {
      return `Ignore patterns must not contain ${describeInvalidChars(ignorePatternInvalidChars)}.`;
    }
  }
  return null;
}

/**
 * Canonical form sent to the backend (BREEZEAI-1238 OBS-02): one trimmed
 * pattern per line, blank lines removed. Returns "" when nothing survives.
 */
export function normalizeIgnorePatterns(value: string): string {
  return value
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0)
    .join("\n");
}

/**
 * Validate an ontology name before sending it to the backend. Mirrors the
 * server-side rules for the rename endpoint (BREEZEAI-1051) so bad input is
 * rejected in the browser instead of producing raw-stored XSS/SQL payloads,
 * HTTP 500s from column-length overflow, or silent no-op saves.
 */
function validateOntologyName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) {
    throw new Error("Name cannot be empty.");
  }
  if (trimmed.length > MAX_ONTOLOGY_NAME_LENGTH) {
    throw new Error(`Name must be ${MAX_ONTOLOGY_NAME_LENGTH} characters or fewer.`);
  }
  if (INVALID_ONTOLOGY_NAME_CHARS.test(trimmed)) {
    throw new Error('Name contains invalid characters. Avoid < > " \' ; and --.');
  }
  return trimmed;
}

/**
 * Fetch code ontologies for a project
 */
async function getCodeOntologies(
  { projectUuid, page = 1, limit = 10, sortName, sortOrder }: GetOntologiesParams,
  token: string,
  fields?: string[]
): Promise<GetOntologiesResponse> {
  let url = `${config.isometricApiUrl}/code-ontology?filters[projectUuid][$eq]=${projectUuid}&page=${page}&limit=${limit}`;
  if (sortName && sortOrder) {
    url += `&sortName=${encodeURIComponent(sortName)}&sortOrder=${sortOrder}`;
  }
  if (fields && fields.length > 0) {
    url += `&selectFields=${fields.join(',')}`;
  }
  const response = await apiFetch(
    url,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to fetch code ontologies.");
  }

  const resp = await response.json();
  return {
    data: resp.data,
    total: resp.total || resp.data?.length || 0
  };
}

/**
 * Create a new code ontology
 */
async function createCodeOntology(
  { file, projectUuid, name, repoUrl, gitToken, gitBranch }: CreateOntologyParams,
  token: string
): Promise<any> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("projectUuid", projectUuid);
  formData.append("name", name);
  if (repoUrl) formData.append("repoUrl", repoUrl);
  if (gitToken) formData.append("gitToken", gitToken);
  if (gitBranch) formData.append("gitBranch", gitBranch);

  const response = await apiFetch(
    `${config.isometricApiUrl}/code-ontology/generate?llmPlatform=${config.llmPlatform}`,
    {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to create code ontology.");
  }

  return response.json();
}

/**
 * Update an existing code ontology (multipart) — used by the standard Edit
 * flow. Supports an optional file upload (error-state recovery) and the
 * basic git fields (repoUrl, gitToken, gitBranch).
 */
async function updateCodeOntology(
  { id, projectUuid, file, name, repoUrl, gitToken, gitBranch }: UpdateOntologyParams,
  token: string
): Promise<any> {
  assertProjectUuid(projectUuid);

  const formData = new FormData();
  if (file) formData.append("file", file);
  if (name !== undefined) formData.append("name", validateOntologyName(name));
  if (repoUrl) formData.append("repoUrl", repoUrl);
  if (gitToken) formData.append("gitToken", gitToken);
  if (gitBranch) formData.append("gitBranch", gitBranch);

  const response = await apiFetch(
    `${config.isometricApiUrl}/code-ontology/update/${id}?projectUuid=${encodeURIComponent(projectUuid)}`,
    {
      method: "PUT",
      body: formData,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to update code ontology.");
  }

  return response.json();
}

/**
 * Update an existing code ontology's git configuration — JSON PUT
 * /code-ontology/{id}. Used by the dedicated Edit Git Config flow.
 */
async function updateCodeOntologyJson(
  { id, projectUuid, name, repoUrl, gitToken, gitBranch, gitProvider, commitId, ignorePatterns }: UpdateOntologyParams,
  token: string
): Promise<any> {
  assertProjectUuid(projectUuid);

  // Backend expects every field present (empty string when not edited).
  // ignorePatterns is only included when the caller manages it (the Edit Git
  // Config dialog); an empty string there clears the stored patterns.
  const body = {
    name: name ?? "",
    repoUrl: repoUrl ?? "",
    gitToken: gitToken ?? "",
    gitBranch: gitBranch ?? "",
    gitProvider: gitProvider ?? "",
    commitId: commitId ?? "",
    ...(ignorePatterns !== undefined ? { ignorePatterns } : {}),
  };

  const response = await apiFetch(
    `${config.isometricApiUrl}/code-ontology/${id}?projectUuid=${encodeURIComponent(projectUuid)}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to update code ontology.");
  }

  return response.json();
}

/**
 * Hook to fetch code ontologies
 */
export function useGetCodeOntologies(
  projectUuid: string,
  page?: number,
  limit?: number,
  fields?: string[],
  refetchInterval?: CodeOntologyRefetchInterval,
  sortName?: string,
  sortOrder?: SortOrder,
) {
  const { getAuthHeaders } = useAuthHeaders();

  return useQuery({
    queryKey: ["code-ontologies", projectUuid, page, limit, fields, sortName, sortOrder],
    queryFn: () => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return getCodeOntologies({ projectUuid, page, limit, sortName, sortOrder }, token, fields);
    },
    enabled: !!projectUuid,
    refetchInterval: refetchInterval ?? false,
  });
}

/**
 * Fetch single code ontology status by ID
 */
async function getCodeOntologyStatus(
  ontologyId: string,
  projectUuid: string,
  token: string
): Promise<{ fileGraphStatus?: FileGraphStatus; _id: string }> {
  const response = await apiFetch(
    `${config.isometricApiUrl}/code-ontology?filters[_id][$eq]=${ontologyId}&filters[projectUuid][$eq]=${projectUuid}&selectFields=fileGraphStatus,_id,clusterCount`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to fetch code ontology status.");
  }

  const resp = await response.json();
  return resp.data?.[0] || { _id: ontologyId };
}

/**
 * Fetch the git token for a single code ontology. The list endpoint hides
 * gitToken by default (entity `select: false`); explicitly requesting it via
 * `selectFields` overrides that so the Edit Git Config popup can prefill it.
 */
export async function fetchCodeOntologyGitToken(
  ontologyId: string | number,
  projectUuid: string,
  token: string
): Promise<string> {
  const response = await apiFetch(
    `${config.isometricApiUrl}/code-ontology?filters[_id][$eq]=${ontologyId}&filters[projectUuid][$eq]=${projectUuid}&selectFields=_id,gitToken`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to fetch code ontology git token.");
  }

  const resp = await response.json();
  return resp.data?.[0]?.gitToken ?? "";
}

/**
 * Hook to create code ontology
 */
export function useCreateCodeOntology(
  onSuccess?: (data: any) => void,
  onError?: (error: Error) => void
) {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CreateOntologyParams) => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return createCodeOntology(params, token);
    },
    onSuccess: (data, variables) => {
      // Any mounted useGetCodeOntologies for this project (e.g. the knowledge
      // graph's Code node) was returning the pre-upload empty list and showing
      // "No repositories…" on click; invalidate so it refetches and the new
      // repo shows up without a hard page refresh.
      queryClient.invalidateQueries({ queryKey: ["code-ontologies", variables.projectUuid] });
      onSuccess?.(data);
    },
    onError,
  });
}

/**
 * Create a code ontology from a git repository
 */
async function createCodeOntologyFromGit(
  { projectUuid, name, repoUrl, gitToken, gitBranch, gitProvider, ignorePatterns }: CreateOntologyFromGitParams,
  token: string
): Promise<any> {
  const response = await apiFetch(
    `${config.isometricApiUrl}/code-ontology/generate-from-git`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ projectUuid, name, repoUrl, ...(gitToken ? { gitToken } : {}), gitBranch, ...(gitProvider ? { gitProvider } : {}), ...(ignorePatterns?.trim() ? { ignorePatterns: normalizeIgnorePatterns(ignorePatterns) } : {}) }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to create code ontology from git.");
  }

  return response.json();
}

/**
 * Hook to create code ontology from git
 */
export function useCreateCodeOntologyFromGit(
  onSuccess?: (data: any) => void,
  onError?: (error: Error) => void
) {
  const { getAuthHeaders } = useAuthHeaders();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CreateOntologyFromGitParams) => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return createCodeOntologyFromGit(params, token);
    },
    onSuccess: (data, variables) => {
      // Mirror useCreateCodeOntology: refresh any mounted list query for this
      // project so freshly-connected git repos show up without a hard refresh.
      queryClient.invalidateQueries({ queryKey: ["code-ontologies", variables.projectUuid] });
      onSuccess?.(data);
    },
    onError,
  });
}

/**
 * Parse a git repo URL and return the provider API URL to verify the branch.
 * Supports GitHub, GitLab, Bitbucket, and Azure DevOps.
 */
function buildGitTestUrl(repoUrl: string, gitBranch: string): { url: string; headers: (token?: string) => Record<string, string>; provider: string } {
  let cleaned = repoUrl.trim().replace(/\.git$/, '').replace(/\/$/, '');

  if (!cleaned.startsWith('https://')) {
    throw new Error('Repository URL must use HTTPS.');
  }

  // GitHub: https://github.com/owner/repo
  const githubMatch = cleaned.match(/github\.com[/:]([^/]+)\/([^/]+)/);
  if (githubMatch) {
    const [, owner, repo] = githubMatch;
    return {
      provider: 'github',
      url: `https://api.github.com/repos/${owner}/${repo}/branches/${encodeURIComponent(gitBranch)}`,
      headers: (token) => ({
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        Accept: 'application/vnd.github+json',
      }),
    };
  }

  // GitLab: https://gitlab.com/owner/repo (or self-hosted)
  const gitlabMatch = cleaned.match(/(https:\/\/[^/]*gitlab[^/]*)\/(.*)/);
  if (gitlabMatch) {
    const [, host, path] = gitlabMatch;
    const projectPath = encodeURIComponent(path);
    return {
      provider: 'gitlab',
      url: `${host}/api/v4/projects/${projectPath}/repository/branches/${encodeURIComponent(gitBranch)}`,
      headers: (token) => ({
        ...(token ? { 'PRIVATE-TOKEN': token } : {}),
      }),
    };
  }

  // Bitbucket: https://bitbucket.org/owner/repo
  const bitbucketMatch = cleaned.match(/bitbucket\.org[/:]([^/]+)\/([^/]+)/);
  if (bitbucketMatch) {
    const [, owner, repo] = bitbucketMatch;
    return {
      provider: 'bitbucket',
      url: `https://api.bitbucket.org/2.0/repositories/${owner}/${repo}/refs/branches/${encodeURIComponent(gitBranch)}`,
      headers: (token): Record<string, string> => {
        if (!token) return {};
        const credentials = token.includes(':') ? token : `x-token-auth:${token}`;
        return { Authorization: `Basic ${btoa(credentials)}` };
      },
    };
  }

  // Azure DevOps (visualstudio.com): https://owner.visualstudio.com/[prefix/]project/_git/repo
  const azureVsMatch = cleaned.match(/([a-zA-Z0-9-]+)\.visualstudio\.com\/(?:[^/]+\/)?([^/]+)\/_git\/([^/]+)/);
  if (azureVsMatch) {
    const [, org, project, repo] = azureVsMatch;
    return {
      provider: 'azure_devops',
      url: `https://${org}.visualstudio.com/${project}/_apis/git/repositories/${repo}/refs?filter=heads/${encodeURIComponent(gitBranch)}&api-version=7.0`,
      headers: (token) => ({
        ...(token ? { Authorization: `Basic ${btoa(':' + token)}` } : {}),
      }),
    };
  }

  // Azure DevOps: https://dev.azure.com/org/project/_git/repo
  const azureMatch = cleaned.match(/dev\.azure\.com\/([^/]+)\/([^/]+)\/_git\/([^/]+)/);
  if (azureMatch) {
    const [, org, project, repo] = azureMatch;
    return {
      provider: 'azure_devops',
      url: `https://dev.azure.com/${org}/${project}/_apis/git/repositories/${repo}/refs?filter=heads/${encodeURIComponent(gitBranch)}&api-version=7.0`,
      headers: (token) => ({
        ...(token ? { Authorization: `Basic ${btoa(':' + token)}` } : {}),
      }),
    };
  }

  // Fallback: assume GitHub-compatible API
  const fallbackMatch = cleaned.match(/https:\/\/[^/]+\/([^/]+)\/([^/]+)/);
  if (fallbackMatch) {
    const [, owner, repo] = fallbackMatch;
    return {
      provider: 'github',
      url: `https://api.github.com/repos/${owner}/${repo}/branches/${encodeURIComponent(gitBranch)}`,
      headers: (token) => ({
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        Accept: 'application/vnd.github+json',
      }),
    };
  }

  throw new Error('Could not parse repository URL. Please check the format.');
}

/**
 * Test git connection by calling the provider API directly
 */
async function testGitConnection(
  { repoUrl, gitToken, gitBranch }: TestGitConnectionParams
): Promise<{ provider: string; branch: string }> {
  const { url, headers, provider } = buildGitTestUrl(repoUrl, gitBranch);

  const response = await fetch(url, {
    method: 'GET',
    headers: headers(gitToken),
  });

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      throw new Error('Authentication failed. Please check your git token.');
    }
    if (response.status === 404) {
      throw new Error('Repository or branch not found. Please check the URL and branch name.');
    }
    throw new Error(`Connection failed with status ${response.status}.`);
  }

  return { provider, branch: gitBranch };
}

/**
 * Hook to test git connection
 */
export function useTestGitConnection(
  onSuccess?: (data: any) => void,
  onError?: (error: Error) => void
) {
  return useMutation({
    mutationFn: (params: TestGitConnectionParams) => {
      return testGitConnection(params);
    },
    onSuccess,
    onError,
  });
}

/**
 * Check for updates on a code ontology
 */
async function checkCodeOntologyUpdate(
  id: string,
  token: string
): Promise<CheckUpdateResponse> {
  const response = await apiFetch(
    `${config.isometricApiUrl}/code-ontology/check-update/${id}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to check for updates.");
  }

  return response.json();
}

/**
 * Hook to check for code ontology updates
 */
export function useCheckCodeOntologyUpdate(
  onSuccess?: (data: CheckUpdateResponse) => void,
  onError?: (error: Error) => void
) {
  const { getAuthHeaders } = useAuthHeaders();

  return useMutation({
    mutationFn: (id: string) => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return checkCodeOntologyUpdate(id, token);
    },
    onSuccess,
    onError,
  });
}

/**
 * Sync a code ontology with latest git changes
 */
async function syncCodeOntology(
  { id, incomingCommitId, projectUuid }: { id: string; incomingCommitId: string; projectUuid: string },
  token: string
): Promise<any> {
  assertProjectUuid(projectUuid);

  const response = await apiFetch(
    `${config.isometricApiUrl}/code-ontology/sync/${id}?projectUuid=${encodeURIComponent(projectUuid)}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ incomingCommitId }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to sync code ontology.");
  }

  return response.json();
}

/**
 * Set the commit the next code diff document starts from (BREEZEAI-1160): the
 * baseline for a repository with no diff document yet. The backend verifies the
 * SHA exists on the repository's branch and stores the full SHA.
 */
async function setOntologyCommit(
  { id, projectUuid, commitId }: SetOntologyCommitParams,
  token: string
): Promise<{ success: boolean; data: { _id: number; ontologyCommitId: string } }> {
  assertProjectUuid(projectUuid);

  const response = await apiFetch(
    `${config.isometricApiUrl}/code-ontology/${id}/ontology-commit?projectUuid=${encodeURIComponent(projectUuid)}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ commitId }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Failed to set the ontology commit.");
  }

  return response.json();
}

interface SetOntologyCommitParams {
  id: string;
  projectUuid: string;
  commitId: string;
}

/**
 * Hook to set the ontology commit (ontologyCommitId) for a code ontology
 */
export function useSetOntologyCommit(
  onSuccess?: (data: any) => void,
  onError?: (error: Error) => void
) {
  const { getAuthHeaders } = useAuthHeaders();

  return useMutation({
    mutationFn: (params: SetOntologyCommitParams) => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return setOntologyCommit(params, token);
    },
    onSuccess,
    onError,
  });
}

/** Why the backend refused to make a diff document; absent for other failures. */
export type DiffDocumentErrorCode =
  | "no_baseline"
  | "code_graph_indexing"
  | "up_to_date"
  | "diff_too_large";

export class DiffDocumentError extends Error {
  constructor(
    message: string,
    public readonly code?: DiffDocumentErrorCode
  ) {
    super(message);
  }
}

/**
 * Create the code diff document for a repository (from the last diff document's
 * commit to the code graph's commit), or return the one that already exists.
 */
async function createDiffDocument(
  { id, projectUuid }: { id: string; projectUuid: string },
  token: string
): Promise<{
  success: boolean;
  data: { reused: boolean; documentId: number; baseCommitId: string; headCommitId: string };
}> {
  assertProjectUuid(projectUuid);

  const response = await apiFetch(
    `${config.isometricApiUrl}/code-ontology/${id}/diff-document?projectUuid=${encodeURIComponent(projectUuid)}`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new DiffDocumentError(
      error.message || "Failed to create the diff document.",
      error.code
    );
  }

  return response.json();
}

/**
 * Hook to create (or reuse) a repository's code diff document
 */
export function useCreateDiffDocument(
  onSuccess?: (data: Awaited<ReturnType<typeof createDiffDocument>>) => void,
  onError?: (error: DiffDocumentError) => void
) {
  const { getAuthHeaders } = useAuthHeaders();

  return useMutation({
    mutationFn: (params: { id: string; projectUuid: string }) => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return createDiffDocument(params, token);
    },
    onSuccess,
    onError: onError as ((error: Error) => void) | undefined,
  });
}

/**
 * Hook to sync code ontology
 */
export function useSyncCodeOntology(
  onSuccess?: (data: any) => void,
  onError?: (error: Error) => void
) {
  const { getAuthHeaders } = useAuthHeaders();

  return useMutation({
    mutationFn: (params: { id: string; incomingCommitId: string; projectUuid: string }) => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return syncCodeOntology(params, token);
    },
    onSuccess,
    onError,
  });
}

/**
 * Hook to update code ontology
 */
export function useUpdateCodeOntology(
  onSuccess?: (data: any) => void,
  onError?: (error: Error) => void
) {
  const { getAuthHeaders } = useAuthHeaders();

  return useMutation({
    mutationFn: (params: UpdateOntologyParams) => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return updateCodeOntology(params, token);
    },
    onSuccess,
    onError,
  });
}

/**
 * Hook to update only the git configuration via JSON PUT /code-ontology/{id}.
 * Drives the dedicated "Edit Git Config" flow on the ontology card.
 */
export function useUpdateCodeOntologyGitConfig(
  onSuccess?: (data: any) => void,
  onError?: (error: Error) => void
) {
  const { getAuthHeaders } = useAuthHeaders();

  return useMutation({
    mutationFn: (params: UpdateOntologyParams) => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return updateCodeOntologyJson(params, token);
    },
    onSuccess,
    onError,
  });
}

/**
 * Fetch just the name / repositoryName for one ontology. Used by the
 * breadcrumb on code-ontology/graph|nodes pages so the current repo is
 * visible instead of a generic "Graph" or "Nodes" label. Also pulls
 * repoUrl and repositoryPath so we can derive the repo name for older
 * ontologies where `repositoryName` was never populated.
 */
async function getCodeOntologyName(
  ontologyId: string,
  projectUuid: string,
  token: string
): Promise<{
  _id: string;
  name?: string;
  repositoryName?: string;
  repoUrl?: string;
  repositoryPath?: string;
}> {
  const response = await apiFetch(
    `${config.isometricApiUrl}/code-ontology?filters[_id][$eq]=${ontologyId}&filters[projectUuid][$eq]=${projectUuid}&selectFields=_id,name,repositoryName,repoUrl,repositoryPath`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch code ontology name");
  }

  const resp = await response.json();
  return resp.data?.[0] || { _id: ontologyId };
}

export function useGetCodeOntologyName(
  ontologyId: string,
  projectUuid: string,
  enabled: boolean = true
) {
  const { getAuthHeaders } = useAuthHeaders();

  return useQuery({
    queryKey: ["code-ontology-name", ontologyId, projectUuid],
    queryFn: () => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return getCodeOntologyName(ontologyId, projectUuid, token);
    },
    enabled: !!ontologyId && !!projectUuid && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to poll code ontology status
 */
export function useGetCodeOntologyStatus(ontologyId: string, projectUuid: string, enabled: boolean = true) {
  const { getAuthHeaders } = useAuthHeaders();

  return useQuery({
    queryKey: ["code-ontology-status", ontologyId, projectUuid],
    queryFn: () => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return getCodeOntologyStatus(ontologyId, projectUuid, token);
    },
    enabled: !!ontologyId && !!projectUuid && enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.fileGraphStatus;
      return status && status !== 'active' ? 3000 : false;
    },
  });
}

/**
 * Delete a code ontology
 */
async function deleteCodeOntology(
  { id, projectUuid }: DeleteOntologyParams,
  token: string
): Promise<any> {
  assertProjectUuid(projectUuid);

  const response = await apiFetch(
    `${config.isometricApiUrl}/code-ontology/${id}?projectUuid=${encodeURIComponent(projectUuid)}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ projectUuid }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to delete code ontology.");
  }

  return response.json();
}

/**
 * Hook to delete code ontology
 */
export function useDeleteCodeOntology(
  onSuccess?: (data: any) => void,
  onError?: (error: Error) => void
) {
  const { getAuthHeaders } = useAuthHeaders();

  return useMutation({
    mutationFn: (params: DeleteOntologyParams) => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return deleteCodeOntology(params, token);
    },
    onSuccess,
    onError,
  });
}

/**
 * Search code ontologies
 */
async function searchCodeOntologies(
  { query, projectUuid, page = 1, limit = 10, sortName, sortOrder }: SearchOntologyParams,
  token: string
): Promise<GetOntologiesResponse> {
  let url = `${config.isometricApiUrl}/code-ontology/search?q=${encodeURIComponent(query)}&page=${page}&limit=${limit}&projectUuid=${projectUuid}`;
  if (sortName && sortOrder) {
    url += `&sortName=${encodeURIComponent(sortName)}&sortOrder=${sortOrder}`;
  }
  const response = await apiFetch(
    url,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to search code ontologies.");
  }

  const resp = await response.json();
  return {
    data: resp.data,
    total: resp.total || resp.data?.length || 0
  };
}

/**
 * Hook to search code ontologies
 */
export function useSearchCodeOntologies(
  query: string,
  projectUuid: string,
  enabled?: boolean,
  page?: number,
  limit?: number,
  sortName?: string,
  sortOrder?: SortOrder,
) {
  const { getAuthHeaders } = useAuthHeaders();

  return useQuery({
    queryKey: ["search-code-ontologies", query, projectUuid, page, limit, sortName, sortOrder],
    queryFn: () => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return searchCodeOntologies({ query, projectUuid, page, limit, sortName, sortOrder }, token);
    },
    enabled: !!enabled && !!projectUuid,
  });
}
